import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
});

// Add token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  logout: () => {
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
  }
};

export const promptAPI = {
  build: async (data) => {
    const response = await api.post('/prompts/build', data);
    return response.data;
  },
  save: async (data) => {
    const response = await api.post('/prompts/save', null, { params: data });
    return response.data;
  },
  getLibrary: async (category = null, skip = 0, limit = 20) => {
    const response = await api.get('/prompts/library', { params: { category, skip, limit } });
    return response.data;
  },
  generateImage: async (prompt) => {
    const response = await api.post('/prompts/generate-image', { prompt });
    return response.data;
  },
  remixImage: async (prompt, imageBase64) => {
    const response = await api.post('/prompts/remix-image', { prompt, image_base64: imageBase64 });
    return response.data;
  },
  composePhotos: async (prompt, imagesBase64) => {
    const response = await api.post('/prompts/compose-photos', { prompt, images_base64: imagesBase64 });
    return response.data;
  }
};

export const aiStudioAPI = {
  process: async (imageBase64, tool, options = {}, modelPreference = "gemini") => {
    const response = await api.post('/ai-studio/process', { 
      image_base64: imageBase64, 
      tool, 
      options,
      model_preference: modelPreference
    }, { timeout: 120000 }); // 2 min timeout
    return response.data;
  }
};

export const chatGPTAPI = {
  assist: async (message, conversationHistory = []) => {
    const response = await api.post('/chatgpt/assist', { 
      message, 
      conversation_history: conversationHistory 
    }, { timeout: 60000 });
    return response.data;
  },
  generateImage: async (prompt) => {
    const response = await api.post('/chatgpt/generate-image', { prompt }, { timeout: 120000 });
    return response.data;
  }
};

export const subscriptionAPI = {
  getStatus: async () => {
    const response = await api.get('/subscription/status');
    return response.data;
  },
  getPlans: async () => {
    const response = await api.get('/subscription/plans');
    return response.data;
  },
  createCheckout: async (planId) => {
    const response = await api.post(`/subscription/create-checkout/${planId}`);
    return response.data;
  },
  verify: async (sessionId, plan) => {
    const response = await api.get(`/subscription/verify/${sessionId}`, { params: { plan } });
    return response.data;
  },
  buyCredits: async (packId) => {
    const response = await api.post(`/subscription/buy-credits/${packId}`);
    return response.data;
  },
  verifyCredits: async (sessionId, packId) => {
    const response = await api.get(`/subscription/verify-credits/${sessionId}`, { params: { pack_id: packId } });
    return response.data;
  },
  buyOverageCredits: async (creditType, quantity) => {
    const response = await api.post('/subscription/buy-credits', { credit_type: creditType, quantity });
    return response.data;
  },
  verifyOverage: async (sessionId, creditType, quantity) => {
    const response = await api.get(`/subscription/verify-overage/${sessionId}`, { 
      params: { credit_type: creditType, quantity } 
    });
    return response.data;
  },
  // Admin endpoints
  getAdminUsers: async () => {
    const response = await api.get('/subscription/admin/users');
    return response.data;
  },
  getAdminStats: async () => {
    const response = await api.get('/subscription/admin/stats');
    return response.data;
  },
  grantLegacyPlan: async (email) => {
    const response = await api.post(`/subscription/admin/grant-legacy/${encodeURIComponent(email)}`);
    return response.data;
  },
  migrateLegacy: async () => {
    const response = await api.post('/subscription/admin/migrate-legacy');
    return response.data;
  },
  addUser: async (data) => {
    const response = await api.post('/subscription/admin/add-user', data);
    return response.data;
  },
  adjustCredits: async (data) => {
    const response = await api.post('/subscription/admin/adjust-credits', data);
    return response.data;
  },
  getActivity: async (days = 7) => {
    const response = await api.get('/subscription/admin/activity', { params: { days } });
    return response.data;
  }
};

export const challengeAPI = {
  getDaily: async () => {
    const response = await api.get('/challenges/daily');
    return response.data;
  },
  submit: async (challengeId, rewrittenPrompt) => {
    const response = await api.post(`/challenges/${challengeId}/submit`, null, { 
      params: { rewritten_prompt: rewrittenPrompt } 
    });
    return response.data;
  }
};

export const leaderboardAPI = {
  get: async (limit = 10) => {
    const response = await api.get('/leaderboard', { params: { limit } });
    return response.data;
  }
};

// Emoji Prompt Studio API
export const emojiAPI = {
  translate: async (emojis) => {
    const response = await api.post('/emoji/translate', { emojis });
    return response.data;
  },
  generate: async (emojis) => {
    const response = await api.post('/emoji/generate', { emojis }, { timeout: 120000 });
    return response.data;
  },
  imageToEmoji: async (imageBase64) => {
    const response = await api.post('/emoji/image-to-emoji', { image_base64: imageBase64 }, { timeout: 60000 });
    return response.data;
  },
  getDictionary: async () => {
    const response = await api.get('/emoji/dictionary');
    return response.data;
  },
  getRecipes: async () => {
    const response = await api.get('/emoji/recipes');
    return response.data;
  },
  // Admin endpoints
  updateEmoji: async (emoji, meaning, category = 'general', styleHints = null) => {
    const response = await api.post('/emoji/dictionary', { emoji, meaning, category, style_hints: styleHints });
    return response.data;
  },
  deleteEmoji: async (emoji) => {
    const response = await api.delete(`/emoji/dictionary/${encodeURIComponent(emoji)}`);
    return response.data;
  },
  updateRecipe: async (recipe) => {
    const response = await api.post('/emoji/recipes', recipe);
    return response.data;
  },
  deleteRecipe: async (recipeId) => {
    const response = await api.delete(`/emoji/recipes/${recipeId}`);
    return response.data;
  }
};

export { api };
export default api;
